import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { pickBookCover } from "#/lib/assets";
import { curatedCover, FEATURED_BOOK_HANDLES } from "#/lib/homepage-curated";
import { ROUTES } from "#/lib/routes";
import { useProducts } from "#/queries/products";
import {
  ArrowBtn,
  BookCover,
  PillBtn,
  SectionHead,
  Skeleton,
} from "../primitives.js";
import { shopSearchForPublishers } from "#/lib/url-search-params"

const SLIDE_TRANSITION =
  "transform 1500ms cubic-bezier(0.68, -0.4, 0.24, 1.2), opacity 1000ms ease-in-out, filter 800ms ease-out 400ms";

const SIDE_OFFSET_PX = 420;
const DESKTOP_TEXT_HEIGHT = 80;
const MOBILE_TEXT_HEIGHT = 140;



export function FeaturedBooks() {
  const { data } = useProducts({
    handles: FEATURED_BOOK_HANDLES,
    limit: FEATURED_BOOK_HANDLES.length,
  });
  const [i, setI] = useState(0);

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const items = data?.items ?? [];
  const total = items.length;
  const center = items[i];

  if (!center) {
    return <FeaturedBooksSkeleton />;
  }

  const handlePrev = () => {
    if (i > 0) setI(i - 1);
  };

  const handleNext = () => {
    if (i < total - 1) setI(i + 1);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handlePrev();
  };

  const firstItem = items[0];
  const lastItem = items[total - 1];

  const extendedItems =
    total > 1 && firstItem && lastItem
      ? [
          { item: lastItem, virtualIdx: -1, key: "clone-last" },
          ...items.map((item, idx) => ({
            item,
            virtualIdx: idx,
            key: item.id,
          })),
          { item: firstItem, virtualIdx: total, key: "clone-first" },
        ]
      : items.map((item, idx) => ({ item, virtualIdx: idx, key: item.id }));

  return (
    <section className="relative pt-12 pb-12 w-full flex flex-col items-center overflow-x-clip">
      <div className="w-full max-w-5xl px-6 lg:px-10">
        <SectionHead>FEATURED BOOKS</SectionHead>
      </div>

      <div
        className="relative h-[280px] sm:h-[380px] lg:h-[590px] w-full max-w-7xl mx-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div
          className={`-translate-y-1/2 absolute top-1/2 left-2 sm:left-6 z-20 transition-opacity duration-300 ${
            i === 0 ? "grayscale pointer-events-none" : "opacity-100"
          }`}
        >
          <ArrowBtn dir="left" variant="filled" onClick={handlePrev} />
        </div>
        <div
          className={`-translate-y-1/2 absolute top-1/2 right-2 sm:right-6 z-20 transition-opacity duration-300 ${
            i === total - 1 ? "grayscale pointer-events-none" : "opacity-100"
          }`}
        >
          <ArrowBtn dir="right" variant="filled" onClick={handleNext} />
        </div>

        <div className="mt-4 md:mt-0 mx-auto h-full w-full max-w-5xl relative">
          {extendedItems.map(({ item, virtualIdx, key }) => {
            const offset = virtualIdx - i;
            const isCenter = offset === 0;
            const isVisible = Math.abs(offset) <= 1;

            return (
              <div
                key={key}
                className={`absolute left-1/2 top-1/2 origin-center flex justify-center items-center ${
                  isCenter
                    ? "opacity-100 z-10"
                    : isVisible
                      ? "opacity-0 lg:opacity-100 z-0"
                      : "opacity-0 pointer-events-none z-0"
                }`}
                style={{
                  transform: `translate(-50%, -50%) translateX(calc(${offset} * clamp(240px, 80vw, ${SIDE_OFFSET_PX}px))) scale(${isCenter ? 1 : 0.6})`,
                  filter: isCenter ? "none" : "blur(4px)",
                  transition: SLIDE_TRANSITION,
                  pointerEvents: isVisible ? "auto" : "none",
                }}
              >
                <div className="w-[180px] h-[264px] sm:w-[240px] sm:h-[352px] lg:w-[355px] lg:h-[520px] transition-all duration-500 flex justify-center items-center">
                  <BookCover
                    src={
                      curatedCover(item.handle)?.src ??
                      item.thumbnail ??
                      pickBookCover(item.id)
                    }
                    srcSet={curatedCover(item.handle)?.srcSet}
                    sizes="(min-width: 1024px) 355px, (min-width: 640px) 240px, 180px"
                    w={355}
                    h={520}
                    alt={item.title}
                    fit="contain"
                    loading={isCenter ? "eager" : "lazy"}
                    fetchPriority={isCenter ? "high" : undefined}
                    className={`!max-h-full !max-w-full ${isCenter ? "cursor-pointer" : ""}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden lg:block w-full max-w-5xl mx-auto">
        <div className="grid w-full items-center gap-12 px-10 grid-cols-[1fr_auto_1fr]">
          <div className="relative h-[120px] w-full overflow-hidden">
            {extendedItems.map(({ item, virtualIdx, key }) => {
              const offset = virtualIdx - i;
              const isCenter = offset === 0;
              const isVisible = Math.abs(offset) <= 1;

              return (
                <div
                  key={`left-${key}`}
                  className="absolute inset-x-0 top-0 flex flex-col text-left justify-center items-start h-full"
                  style={{
                    transform: `translateY(${offset * DESKTOP_TEXT_HEIGHT}px)`,
                    opacity: isCenter ? 1 : isVisible ? 0 : 0,
                    filter: isCenter ? "none" : "blur(1px)",
                    transition: SLIDE_TRANSITION,
                    pointerEvents: isCenter ? "auto" : "none",
                  }}
                >
                  <div className="mb-1.5 font-display text-sm font-medium text-[#6F6E6E] uppercase tracking-wide">
                    {item.metadata.series ?? "Learning Plus"}
                  </div>
                  <h3 className="m-0 mb-2.5 max-w-[360px] font-bold font-display text-2xl text-[#6F6E6E] leading-tight line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="font-display text-sm text-[#6F6E6E]">
                    {item.metadata.gradeLabel ?? ""}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center relative z-10">
            <Link
              to={ROUTES.shop}
              search={shopSearchForPublishers(center.metadata.publisher)}
            >
              <PillBtn variant="green">Shop Now</PillBtn>
            </Link>
          </div>

          <div className="relative h-[120px] w-full overflow-hidden">
            {extendedItems.map(({ item, virtualIdx, key }) => {
              const offset = virtualIdx - i;
              const isCenter = offset === 0;
              const isVisible = Math.abs(offset) <= 1;

              return (
                <div
                  key={`right-${key}`}
                  className="absolute inset-x-0 top-0 flex flex-col justify-center items-start text-left h-full"
                  style={{
                    transform: `translateY(${offset * DESKTOP_TEXT_HEIGHT}px)`,
                    opacity: isCenter ? 1 : isVisible ? 0.15 : 0,
                    filter: isCenter ? "none" : "blur(1px)",
                    transition: SLIDE_TRANSITION,
                    pointerEvents: isCenter ? "auto" : "none",
                  }}
                >
                  <p className="m-0 max-w-[360px] font-display text-sm text-[#6F6E6E] leading-relaxed line-clamp-4">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Text Blocks */}
      <div className="block lg:hidden w-full max-w-sm mx-auto mt-6 px-6">
        <div className="relative h-[140px] w-full overflow-hidden">
          {extendedItems.map(({ item, virtualIdx, key }) => {
            const offset = virtualIdx - i;
            const isCenter = offset === 0;
            const isVisible = Math.abs(offset) <= 1;

            return (
              <div
                key={`mob-txt-${key}`}
                className="absolute inset-x-0 top-0 flex flex-col items-center justify-center text-center h-full"
                style={{
                  transform: `translateY(${offset * MOBILE_TEXT_HEIGHT}px)`,
                  opacity: isCenter ? 1 : isVisible ? 0.15 : 0,
                  filter: isCenter ? "none" : "blur(1px)",
                  transition: SLIDE_TRANSITION,
                  pointerEvents: isCenter ? "auto" : "none",
                }}
              >
                <h3 className="m-0 mb-3 max-w-[280px] font-bold font-display text-2xl text-[#6F6E6E] leading-tight line-clamp-2">
                  {item.title}
                </h3>
                <p className="m-0 max-w-[300px] font-display text-sm text-[#6F6E6E] leading-relaxed line-clamp-3">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center relative z-10 mt-2">
          <Link
            to={ROUTES.shop}
            search={shopSearchForPublishers(center.metadata.publisher)}
          >
            <PillBtn variant="green" size="sm">
              Shop Now
            </PillBtn>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturedBooksSkeleton() {
  return (
    <section
      className="relative pt-12 pb-12 w-full flex flex-col items-center overflow-x-clip"
      aria-label="Loading featured books"
      role="status"
    >
      <div className="w-full max-w-5xl px-6 lg:px-10">
        <SectionHead>FEATURED BOOKS</SectionHead>
      </div>

      <div className="relative mt-8 h-[280px] sm:h-[380px] lg:h-[590px] w-full max-w-7xl mx-auto">
        <div className="mx-auto h-full w-full max-w-5xl relative">
          {[-1, 0, 1].map((offset) => (
            <div
              key={offset}
              className={`absolute left-1/2 top-1/2 origin-center flex justify-center items-center ${
                offset === 0
                  ? "opacity-100 z-10"
                  : "opacity-0 lg:opacity-40 z-0"
              }`}
              style={{
                transform: `translate(-50%, -50%) translateX(calc(${offset} * clamp(240px, 80vw, ${SIDE_OFFSET_PX}px))) scale(${
                  offset === 0 ? 1 : 0.6
                })`,
                filter: offset === 0 ? "none" : "blur(4px)",
              }}
            >
              <div className="w-[180px] sm:w-[240px] lg:w-[355px] aspect-[355/520]">
                <Skeleton className="w-full h-full rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="hidden lg:block w-full max-w-5xl mx-auto">
        <div className="mt-8 grid w-full items-center gap-12 px-10 grid-cols-[1fr_auto_1fr]">
          <div className="relative h-[50px] w-full flex flex-col text-left justify-center items-start">
            <Skeleton className="mb-2 h-4 w-28" />
            <Skeleton className="mb-2.5 h-8 w-3/4 max-w-[360px]" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex justify-center">
            <Skeleton className="h-12 w-36 rounded-full" />
          </div>
          <div className="relative h-[50px] w-full flex flex-col items-start justify-center space-y-2">
            <Skeleton className="h-4 w-full max-w-[360px]" />
            <Skeleton className="h-4 w-5/6 max-w-[300px]" />
            <Skeleton className="h-4 w-2/3 max-w-[240px]" />
          </div>
        </div>
      </div>

      <div className="block lg:hidden w-full max-w-sm mx-auto mt-6 px-6">
        <div className="relative h-[140px] w-full flex flex-col items-center justify-center gap-3">
          <Skeleton className="h-7 w-3/4 max-w-[280px]" />
          <Skeleton className="h-3 w-full max-w-[300px]" />
          <Skeleton className="h-3 w-5/6 max-w-[260px]" />
        </div>
        <div className="flex justify-center mt-2">
          <Skeleton className="h-10 w-32 rounded-full" />
        </div>
      </div>
    </section>
  );
}
